import { useEffect, useRef } from "react";
import { attemptClientReloadRecovery } from "@/lib/reloadRecovery";
import { isDirectorToolInFlight } from "@/lib/directorActivity";

const META_SELECTOR = 'meta[name="build-version"]';
const MIN_CHECK_INTERVAL_MS = 30_000;

const readBuildVersion = (html?: string): string | null => {
  if (typeof document === "undefined") return null;
  if (html) {
    const match = html.match(
      /<meta\s+name=["']build-version["']\s+content=["']([^"']+)["']/i,
    );
    return match?.[1] ?? null;
  }
  return (
    document.querySelector(META_SELECTOR)?.getAttribute("content") ?? null
  );
};

/**
 * Reloads the page when the deployed build changes mid-session.
 *
 * Lazy-loaded chunks compiled against a newer deploy can pull duplicate
 * copies of @trpc/react-query and Radix UI into a session that already
 * mounted Providers from the older build, producing "Unable to find tRPC
 * Context" / "DialogPortal must be used within Dialog" errors.
 *
 * On each route change after the first, we fetch /index.html and compare
 * the build-version meta tag. If it changed, reload immediately so the
 * next lazy route render can't mix old providers with new chunks.
 */
export function useBuildVersionReload(location: string) {
  const sessionVersionRef = useRef<string | null>(null);
  const lastCheckRef = useRef(0);
  const isFirstRunRef = useRef(true);

  useEffect(() => {
    if (sessionVersionRef.current === null) {
      sessionVersionRef.current = readBuildVersion();
    }

    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      return;
    }

    if (!sessionVersionRef.current) return;

    const now = Date.now();
    if (now - lastCheckRef.current < MIN_CHECK_INTERVAL_MS) return;
    lastCheckRef.current = now;

    const controller = new AbortController();
    fetch("/index.html", {
      cache: "no-store",
      signal: controller.signal,
      credentials: "same-origin",
    })
      .then(res => (res.ok ? res.text() : null))
      .then(html => {
        if (!html) return;
        const latest = readBuildVersion(html);
        if (!latest) return;
        if (latest !== sessionVersionRef.current) {
          // Don't reload out from under an in-flight Director tool: doing so
          // severs the stream and shows "the browser reloaded before it
          // received this tool result", stranding partial render progress.
          // Leave sessionVersionRef stale so the next route change / poll
          // re-detects the mismatch and reloads once the tool settles.
          if (isDirectorToolInFlight()) return;
          attemptClientReloadRecovery("build-version changed");
        }
      })
      .catch(() => {});

    return () => controller.abort();
  }, [location]);
}
